import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { ResolvedSetting } from "@/api/integrations";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { CELL_ROLLUP_KEY, CELL_SHARE_KEY } from "@/modules/settings/components/CellRollupCard";
import { SourcePill } from "@/modules/settings/components/SourcePill";
import {
  usePutTenantIntegration,
  useTenantIntegration,
  type IntegrationCategory,
} from "@/queries/integrations";

interface Props {
  category: IntegrationCategory;
  i18nTitleKey: string;
  i18nSubtitleKey: string;
}

/**
 * Detection is tenant-tier-only. This page lists the resolved keys with
 * editable values; per-block detection overrides live on the block grid
 * config card.
 *
 * It also served the email + webhook categories until public migration
 * 0048 dropped their keys as inert.
 */
export function IntegrationsTenantOnlyPage({
  category,
  i18nTitleKey,
  i18nSubtitleKey,
}: Props): ReactNode {
  const { t } = useTranslation("integrations");
  const tenantQ = useTenantIntegration(category);
  const putTenant = usePutTenantIntegration(category);

  // The two block-health keys share this category on the server, but they
  // are edited on Settings → Block health, so they stay out of this list.
  const all = tenantQ.data?.settings ?? [];
  const settings = all.filter((s) => s.key !== CELL_ROLLUP_KEY && s.key !== CELL_SHARE_KEY);

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t(i18nTitleKey)} subtitle={t(i18nSubtitleKey)} />

      <Card noPadding className="p-4">
        {tenantQ.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : tenantQ.isError ? (
          <p className="text-sm text-ap-crit">{t("loadFailed")}</p>
        ) : settings.length === 0 ? (
          <p className="text-sm text-ap-muted">{t("noSettings")}</p>
        ) : (
          <div className="divide-y divide-ap-line">
            {settings.map((s) => (
              <SettingRow
                key={s.key}
                setting={s}
                onSave={(key, value) => putTenant.mutate({ key, value })}
                isPending={putTenant.isPending}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function SettingRow({
  setting,
  onSave,
  isPending,
}: {
  setting: ResolvedSetting;
  onSave: (key: string, value: unknown) => void;
  isPending: boolean;
}): ReactNode {
  const { t } = useTranslation("integrations");
  const [draft, setDraft] = useState(() => formatValue(setting.value));
  useEffect(() => setDraft(formatValue(setting.value)), [setting.value]);
  const dirty = draft !== formatValue(setting.value);
  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <code className="font-mono text-xs text-ap-muted">{setting.key}</code>
      <SourcePill source={setting.source} />
      <input
        className="flex-1 rounded-md border border-ap-line bg-white px-2 py-1 text-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <button
        type="button"
        disabled={!dirty || isPending}
        onClick={() => onSave(setting.key, parseValue(draft))}
        className="rounded-md bg-ap-primary px-2 py-1 text-xs font-medium text-white hover:bg-ap-primary/90 disabled:opacity-60"
      >
        {t("save")}
      </button>
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function parseValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const asNum = Number(trimmed);
  if (!Number.isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(trimmed)) return asNum;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
}
